import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bell, BellOff, Check, CheckCircle2, AlertCircle, MessageSquare,
    UserPlus, ArrowRightCircle, Clock, AtSign, Settings,
    Building2, Inbox, Home, RotateCcw, Shuffle, Volume2, VolumeX
} from 'lucide-react';
import { CustomBreadcrumb, CustomModal, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomPagination } from '@/shared/components/molecules';
import { useAuthStore } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import { notificationsApi } from '../api/notificationsApi';
import { notificationSound } from '@/shared/utils/soundUtil';
import './Notifications.css';

type NotificationType =
    | 'ticket_assigned'
    | 'ticket_resolved'
    | 'ticket_escalated'
    | 'ticket_reassigned'
    | 'comment_added'
    | 'sla_breach'
    | 'mention'
    | 'system'
    | 'school_added'
    | 'employee_added'
    | 'user_invited';

interface NotificationItem {
    id: string;
    type: string;
    title: string;
    message: string;
    is_read: boolean;
    created_at: string;
    actor_name?: string | null;
    link?: string | null;
}

type FilterType = 'all' | 'unread' | NotificationType;

const TYPE_META: Record<NotificationType, { color: string; bg: string; icon: React.ReactNode; label: string }> = {
    ticket_assigned:   { color: '#6366f1', bg: 'rgba(99,102,241,0.12)',  icon: <Inbox size={18} />,           label: 'Assigned' },
    ticket_resolved:   { color: '#10b981', bg: 'rgba(16,185,129,0.12)',  icon: <CheckCircle2 size={18} />,    label: 'Resolved' },
    ticket_escalated:  { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   icon: <AlertCircle size={18} />,     label: 'Escalated' },
    ticket_reassigned: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)',  icon: <Shuffle size={18} />,         label: 'Reassigned' },
    comment_added:     { color: '#0ea5e9', bg: 'rgba(14,165,233,0.12)',  icon: <MessageSquare size={18} />,   label: 'Comments' },
    sla_breach:        { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  icon: <Clock size={18} />,           label: 'Overdue' },
    mention:           { color: '#ec4899', bg: 'rgba(236,72,153,0.12)',  icon: <AtSign size={18} />,          label: 'Mentions' },
    system:            { color: '#64748b', bg: 'rgba(100,116,139,0.12)', icon: <Settings size={18} />,        label: 'System' },
    school_added:      { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',   icon: <Building2 size={18} />,       label: 'Schools' },
    employee_added:    { color: '#10b981', bg: 'rgba(16,185,129,0.12)',  icon: <UserPlus size={18} />,        label: 'Members' },
    user_invited:      { color: '#a855f7', bg: 'rgba(168,85,247,0.12)',  icon: <UserPlus size={18} />,        label: 'Invitations' },
};

const formatRelativeTime = (iso: string): string => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const Notifications: React.FC = () => {
    const navigate = useNavigate();
    const { user, tenant } = useAuthStore();
    const { showToast } = useToastStore();
    const role = user?.role?.toUpperCase() ?? 'SCHOOL_ADMIN';
    const isSuperAdmin = role === 'SUPER_ADMIN';
    const schoolSlug = tenant?.slug || user?.school?.slug || (isSuperAdmin ? 'super-admin' : '');

    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<FilterType>('all');
    const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSoundEnabled, setIsSoundEnabled] = useState(() => notificationSound.isSoundEnabled());

    // Pagination States
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);
    const totalPages = Math.ceil(total / pageSize);

    const handleToggleSound = () => {
        const next = notificationSound.toggleSound();
        setIsSoundEnabled(next);
        showToast(next ? 'Notification sound enabled' : 'Notification sound muted', 'info', 'Sound Settings');
    };

    useEffect(() => {
        const fetchNotifications = async () => {
            setIsLoading(true);
            setError(null);

            if (!schoolSlug) {
                setNotifications([]);
                setError('No school context found.');
                setIsLoading(false);
                return;
            }

            try {
                const response = await notificationsApi.getNotifications(schoolSlug, { page, page_size: pageSize }, isSuperAdmin);
                if (response.success && response.data) {
                    const data = response.data as any;
                    const items: NotificationItem[] = Array.isArray(data) ? data : (data.items || []);
                    setNotifications(items);
                    setTotal(Array.isArray(data) ? data.length : (data.total || data.total_count || 0));
                    setHasNext(Array.isArray(data) ? false : (data.has_next || false));
                    setHasPrevious(Array.isArray(data) ? false : (data.has_previous || false));

                    // Inform Header about unread status without triggering an extra API call
                    const hasUnread = items.some((n) => !n.is_read);
                    window.dispatchEvent(new CustomEvent('notifications_read_update', { detail: { hasUnread } }));
                } else {
                    setNotifications([]);
                    setError(response.error || 'Failed to load notifications');
                }
            } catch (err) {
                setNotifications([]);
                setError('Unable to connect to the server. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchNotifications();
    }, [schoolSlug, role, page, pageSize]);

    const unreadCount = notifications.filter(n => !n.is_read).length;

    // Build dynamic filter tabs from notification types present
    const availableTypes = useMemo(() => {
        const types = new Set<NotificationType>();
        notifications.forEach(n => types.add(n.type as NotificationType));
        return Array.from(types).filter(t => TYPE_META[t]);
    }, [notifications]);

    const filteredNotifications = useMemo(() => {
        if (filter === 'all') return notifications;
        if (filter === 'unread') return notifications.filter(n => !n.is_read);
        return notifications.filter(n => n.type === filter);
    }, [notifications, filter]);

    const handleNotificationClick = async (n: NotificationItem) => {
        setSelectedNotification(n);
        setIsModalOpen(true);

        if (!n.is_read) {
            // Optimistic update
            const updated = notifications.map(item => item.id === n.id ? { ...item, is_read: true } : item);
            setNotifications(updated);
            window.dispatchEvent(new CustomEvent('notifications_read_update', {
                detail: { hasUnread: updated.some(item => !item.is_read) }
            }));
            if (schoolSlug) {
                const response = await notificationsApi.markAsRead(schoolSlug, n.id, isSuperAdmin);
                if (!response.success) {
                    const reverted = notifications.map(item => item.id === n.id ? { ...item, is_read: false } : item);
                    setNotifications(reverted);
                    window.dispatchEvent(new CustomEvent('notifications_read_update', {
                        detail: { hasUnread: reverted.some(item => !item.is_read) }
                    }));
                    showToast(response.error || 'Failed to mark as read', 'error');
                }
            }
        }
    };

    const handleMarkAllRead = async () => {
        const unread = notifications.filter(n => !n.is_read);
        if (unread.length === 0) return;
        // Optimistic update
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        window.dispatchEvent(new CustomEvent('notifications_read_update', { detail: { hasUnread: false } }));
        // Call API for each unread (best-effort)
        if (schoolSlug) {
            await Promise.allSettled(unread.map(n => notificationsApi.markAsRead(schoolSlug, n.id, isSuperAdmin)));
        }
    };

    const subtitle =
        role === 'SUPER_ADMIN' ? 'Platform-wide alerts, schools, and system updates' :
        role === 'EMPLOYEE'    ? 'Your assigned tickets, replies, and mentions' :
                                 'Activity across your school workspace';

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Notifications', icon: <Bell size={14} /> },
                ]}
            />

            <CustomCommonScreenHeader
                title="Notifications"
                subtitle={subtitle}
                icon={<Bell size={24} />}
                actionLabel={unreadCount > 0 ? `Mark all as read` : undefined}
                actionIcon={<Check size={16} />}
                onActionClick={unreadCount > 0 ? handleMarkAllRead : undefined}
            />

            {/* Filter Tabs & Sound Settings */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div className="notif-tabs" style={{ marginBottom: 0 }}>
                    <button
                        className={`notif-tab ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        All
                        <span className="notif-tab-count">{notifications.length}</span>
                    </button>
                    <button
                        className={`notif-tab ${filter === 'unread' ? 'active' : ''}`}
                        onClick={() => setFilter('unread')}
                    >
                        Unread
                        <span className="notif-tab-count">{unreadCount}</span>
                    </button>
                    {availableTypes.map(type => {
                        const meta = TYPE_META[type];
                        const count = notifications.filter(n => n.type === type).length;
                        return (
                            <button
                                key={type}
                                className={`notif-tab ${filter === type ? 'active' : ''}`}
                                onClick={() => setFilter(type)}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', color: meta.color }}>
                                    {React.cloneElement(meta.icon as React.ReactElement, { size: 13 })}
                                </span>
                                {meta.label}
                                <span className="notif-tab-count">{count}</span>
                            </button>
                        );
                    })}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                        type="button"
                        onClick={handleToggleSound}
                        title={isSoundEnabled ? 'Mute notification sounds' : 'Enable notification sounds'}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            padding: '0.4rem 0.75rem',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            background: isSoundEnabled ? 'rgba(99, 102, 241, 0.08)' : 'var(--bg-secondary)',
                            color: isSoundEnabled ? 'var(--accent-primary)' : 'var(--text-muted)',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isSoundEnabled ? <Volume2 size={15} /> : <VolumeX size={15} />}
                        <span>{isSoundEnabled ? 'Sound On' : 'Sound Muted'}</span>
                    </button>
                </div>
            </div>

            {/* List */}
            {isLoading ? (
                <div className="notif-list">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="notif-item" style={{ pointerEvents: 'none' }}>
                            <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)', backgroundSize: '800px 100%', animation: 'pulse 1.5s ease-in-out infinite', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ height: '14px', width: '40%', borderRadius: '4px', background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)', backgroundSize: '800px 100%', animation: 'pulse 1.5s ease-in-out infinite', marginBottom: '0.5rem' }} />
                                <div style={{ height: '10px', width: '70%', borderRadius: '4px', background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)', backgroundSize: '800px 100%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : error ? (
                <div className="notif-empty" style={{ borderColor: 'rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.03)' }}>
                    <div className="notif-empty-icon" style={{ background: 'rgba(239,68,68,0.1)' }}>
                        <AlertCircle size={28} color="#ef4444" />
                    </div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.4rem' }}>
                        Couldn't load notifications
                    </h3>
                    <p style={{ fontSize: '0.85rem', maxWidth: '360px', margin: 0 }}>
                        {error}
                    </p>
                </div>
            ) : filteredNotifications.length === 0 ? (
                <div className="notif-empty">
                    <div className="notif-empty-icon">
                        <BellOff size={28} color="var(--text-muted)" />
                    </div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.4rem' }}>
                        You're all caught up
                    </h3>
                    <p style={{ fontSize: '0.85rem', maxWidth: '320px', margin: 0 }}>
                        {filter === 'unread' ? 'No unread notifications right now.' : 'No notifications matching this filter.'}
                    </p>
                </div>
            ) : (
                <div className="notif-list">
                    {filteredNotifications.map(n => {
                        const meta = TYPE_META[n.type as NotificationType] || TYPE_META.system;
                        return (
                            <div
                                key={n.id}
                                className={`notif-item ${!n.is_read ? 'unread' : ''}`}
                                onClick={() => handleNotificationClick(n)}
                            >
                                <div className="notif-icon" style={{ background: meta.bg, color: meta.color }}>
                                    {meta.icon}
                                </div>
                                <div className="notif-content">
                                    <div className="notif-title">
                                        {!n.is_read && <span className="notif-dot" />}
                                        {n.title}
                                    </div>
                                    <div className="notif-message">{n.message}</div>
                                    <div className="notif-meta">
                                        <Clock size={11} />
                                        <span>{formatRelativeTime(n.created_at)}</span>
                                        {n.actor_name && (
                                            <>
                                                <span className="notif-meta-dot">•</span>
                                                <span>by {n.actor_name}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <ArrowRightCircle size={16} color="var(--text-muted)" style={{ alignSelf: 'center', flexShrink: 0, opacity: 0.5 }} />
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Unified Custom Pagination */}
            <div style={{ marginTop: '1.5rem', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
                <CustomPagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalCount={total}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    hasPrevious={hasPrevious}
                    hasNext={hasNext}
                    isLoading={isLoading}
                    itemName="notifications"
                />
            </div>

            {selectedNotification && (
                <CustomModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title="Notification Detail"
                    size="small"
                    footer={
                        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                            <CustomButton
                                variant="filled"
                                size="small"
                                onClick={() => setIsModalOpen(false)}
                            >
                                Close
                            </CustomButton>
                        </div>
                    }
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '0.5rem 0' }}>
                        {(() => {
                            const meta = TYPE_META[selectedNotification.type as NotificationType] || TYPE_META.system;
                            return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        background: meta.bg,
                                        color: meta.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        {meta.icon}
                                    </div>
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                                            {selectedNotification.title}
                                        </h4>
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                            {meta.label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })()}
                        
                        <p style={{
                            fontSize: '0.9rem',
                            color: 'var(--text-secondary)',
                            lineHeight: 1.5,
                            margin: 0,
                            background: 'var(--bg-secondary)',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)'
                        }}>
                            {selectedNotification.message}
                        </p>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            <Clock size={12} />
                            <span>{new Date(selectedNotification.created_at).toLocaleString()}</span>
                            {selectedNotification.actor_name && (
                                <>
                                    <span>•</span>
                                    <span>By {selectedNotification.actor_name}</span>
                                </>
                            )}
                        </div>

                        {selectedNotification.link && (
                            <CustomButton
                                variant="outline"
                                size="small"
                                onClick={() => {
                                    setIsModalOpen(false);
                                    navigate(selectedNotification.link!);
                                }}
                                style={{ marginTop: '0.5rem', alignSelf: 'flex-start' }}
                            >
                                View Linked Activity
                            </CustomButton>
                        )}
                    </div>
                </CustomModal>
            )}
        </div>
    );
};
